import { useState, useEffect } from 'react';
import { Key, Plus, Trash2, RefreshCw, CheckCircle } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { toast } from 'sonner';

const PROVIDERS = ['openai', 'anthropic', 'google', 'custom'] as const;

export function ApiKeys() {
 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [keys, setKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', provider: 'openai', description: '' });

  useEffect(() => {
    fetchKeys();
  }, []);

  const fetchKeys = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('id, user_id, key_name, provider, is_active, last_used_at, created_at')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setKeys(data || []);
    } catch (err) {
      console.error('Error:', err);
      setKeys([]);
      toast.error('Erro ao carregar chaves');
    } finally {
      setLoading(false);
    }
  };

  const revokeKey = async (id: string) => {
    if (!confirm('Revogar esta chave? O cliente precisará criar uma nova.')) return;
    try {
      const { error } = await supabase.from('api_keys').delete().eq('id', id);
      if (error) throw error;
      toast.success('Chave revogada');
      fetchKeys();
    } catch {
      toast.error('Erro ao revogar');
    }
  };

  const handleCreateKey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!createForm.name.trim()) {
      toast.error('Informe o nome da chave');
      return;
    }
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from('api_keys').insert({
        key_name: createForm.name.trim(),
        provider: createForm.provider,
        user_id: user?.id ?? null,
        is_active: true,
      });
      if (error) throw error;
      toast.success('Chave criada');
      setShowCreateModal(false);
      setCreateForm({ name: '', provider: 'openai', description: '' });
      fetchKeys();
    } catch {
      toast.error('Erro ao criar chave');
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">API Keys</h1>
          <p className="text-slate-400">Gestão de chaves de API dos clientes</p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 admin-gradient rounded-lg text-white font-medium"
        >
          <Plus className="w-4 h-4" />
          Nova Chave
        </button>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowCreateModal(false)}>
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Nova Chave API</h3>
            <form onSubmit={handleCreateKey} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-2">Nome</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: Produção"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Provider</label>
                <select
                  value={createForm.provider}
                  onChange={(e) => setCreateForm((p) => ({ ...p, provider: e.target.value }))}
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  {PROVIDERS.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-2">Descrição (opcional)</label>
                <input
                  type="text"
                  value={createForm.description}
                  onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Uso da chave"
                  className="w-full px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white focus:outline-none focus:border-red-500"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-slate-300 hover:bg-white/10"
                >
                  Cancelar
                </button>
                <button type="submit" className="flex-1 px-4 py-2 admin-gradient rounded-lg text-white font-medium">
                  Criar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <Key className="w-8 h-8 text-violet-400 mb-3" />
          <p className="text-sm text-slate-400">Total de Chaves</p>
          <p className="text-3xl font-bold text-white">{keys.length}</p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <CheckCircle className="w-8 h-8 text-emerald-400 mb-3" />
          <p className="text-sm text-slate-400">Chaves Ativas</p>
          <p className="text-3xl font-bold text-white">{keys.filter(k => k.is_active).length}</p>
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
          <RefreshCw className="w-8 h-8 text-amber-400 mb-3" />
          <p className="text-sm text-slate-400">Total</p>
          <p className="text-3xl font-bold text-white">{keys.length}</p>
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-8 h-8 animate-spin text-red-500" />
          </div>
        ) : keys.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Key className="w-12 h-12 mb-4 opacity-50" />
            <p>Nenhuma chave cadastrada</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">Nome</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">Provider</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">Preview</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">Status</th>
                <th className="text-left py-4 px-6 text-xs font-medium text-slate-400 uppercase">Último Uso</th>
                <th className="text-right py-4 px-6 text-xs font-medium text-slate-400 uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {keys.map((key) => (
                <tr key={key.id} className="hover:bg-white/5">
                  <td className="py-4 px-6 text-white font-medium">{key.key_name}</td>
                  <td className="py-4 px-6">
                    <span className="px-2.5 py-1 bg-violet-500/20 text-violet-400 rounded-full text-xs capitalize">
                      {key.provider}
                    </span>
                  </td>
                  <td className="py-4 px-6">
                    <code className="text-sm text-slate-400 font-mono">•••••••• (valor nunca exposto)</code>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2.5 py-1 rounded-full text-xs ${key.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                      {key.is_active ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-slate-400 text-sm">
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString('pt-BR') : 'Nunca'}
                  </td>
                  <td className="py-4 px-6 text-right">
                    <button
                      onClick={(e) => { e.stopPropagation(); revokeKey(key.id); }}
                      className="p-2 hover:bg-red-500/20 rounded-lg text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

